import React, { useCallback } from 'react';
import { useSidebarLayoutContext } from 'controllers/SidebarLayout/SidebarLayout';
import {
    BadgeSize,
    useBreakpoint,
    Group,
    Badge,
    MaestroComponent,
    IconProps,
    Tooltip,
    ConditionalWrapper,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { useWasTrueOnce } from 'hooks/useWasTrueOnce/useWasTrueOnce';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';
import { useSidebarDisabledEffect } from '../FilterPanes/hooks/useSidebarDisabledEffect';
import useAdvancedFiltersToggle from './useAdvancedFiltersToggle';
import * as Styled from './styled';

const _AdvancedFiltersToggle = ({
    icon,
    isSidebarOpen,
    isSidebarDisabled,
    size,
    total,
    isLoading,
    toggleSidebar,
}: {
    icon: MaestroComponent<IconProps, Record<string, any>>;
    isSidebarOpen: boolean;
    isSidebarDisabled: boolean;
    size: BadgeSize;
    total: number;
    isLoading: boolean;
    toggleSidebar: () => void;
}) => (
    <ConditionalWrapper
        condition={isSidebarDisabled}
        wrapper={(children: React.ReactNode): React.ReactNode => (
            <Tooltip content={t`Filters unavailable`} hasArrow position="top">
                {/**
                 * Needs to wrap in a hidden badge for appropriate size.
                 * Button doesn't trigger tooltip when disabled
                 * https://github.com/LandrAudio/LANDR.Maestro/issues/1460
                 **/}
                <Badge size={size} style={{ visibility: 'hidden' }}>
                    {children}
                </Badge>
            </Tooltip>
        )}
    >
        <Styled.ButtonWithActiveStyle
            icon={icon}
            isSelected={isSidebarOpen}
            onClick={toggleSidebar}
            variant="ghost"
            isDisabled={isSidebarDisabled || isLoading}
            isRounded
            size={size}
        >
            <Group gutter="xs">
                <Group.Item>
                    <Trans>Advanced filters</Trans>
                </Group.Item>
                {!!total && !isSidebarDisabled && (
                    <Group.Item display="flex" alignSelf="center">
                        <Badge
                            content={total.toString()}
                            variant="inverted"
                            isRounded
                            size="sm"
                        />
                    </Group.Item>
                )}
            </Group>
        </Styled.ButtonWithActiveStyle>
    </ConditionalWrapper>
);

const AdvancedFiltersToggle: React.FC<{ onSidebarOpen?: () => void }> = ({
    onSidebarOpen,
}) => {
    const isMobile = useBreakpoint('md');

    const { isSearchBarDisabled } = useSearchBarContext();

    const {
        bpmlowerbound,
        bpmupperbound,
        genres,
        instruments,
        keyquality,
        keyrootnote,
        sfxs,
    } = useCurrentSearchState();

    const { filtersLoading, samplesLoading, packsLoading, isReady } =
        useSearchController();

    const { toggleSidebar, isSidebarOpen, isSidebarDisabled } =
        useSidebarLayoutContext();

    const isLoading = filtersLoading || samplesLoading || packsLoading;
    const wasReadyOnce = useWasTrueOnce(isReady);

    useSidebarDisabledEffect(!wasReadyOnce && isLoading);

    const toggle = useCallback(() => {
        toggleSidebar();

        if (!isSidebarOpen && !isSidebarDisabled) {
            onSidebarOpen?.();
        }
    }, [isSidebarOpen, isSidebarDisabled, onSidebarOpen, toggleSidebar]);

    const { total, icon, size } = useAdvancedFiltersToggle({
        bpmlowerbound,
        bpmupperbound,
        genres,
        instruments,
        keyquality,
        keyrootnote,
        sfxs,
        isSidebarOpen,
        isMobile,
        isLoading: !wasReadyOnce && isLoading,
    });

    return (
        <_AdvancedFiltersToggle
            icon={icon}
            isSidebarOpen={isSidebarOpen && !isSearchBarDisabled}
            size={size}
            total={total}
            toggleSidebar={toggle}
            isSidebarDisabled={isSidebarDisabled || isSearchBarDisabled}
            isLoading={!wasReadyOnce && isLoading}
        />
    );
};

export default AdvancedFiltersToggle;
